import uuid from "uuid";
import { DeviceConfig, DeviceTypes, HardwareConfig, HardwareConfigRecord, AllConfig } from "../../config";
import { PoolDeviceInterface } from "../Interfaces";
import { DisplayProcessingLogics, PoolsideDaemonDeviceStateNamesCommon, StatesToDisplayItem, PoolsideDaemonDeviceStateValues, PoolDeviceCriticalDesiredActual, PoolDevicePropertyAndValue, PoolDeviceDataToDisplay, PoolDevicePropertyToDisplay, PoolDeviceCriticalFieldPropertyAndValue, StateMap, criticalFieldSummaryModes } from "../Types";
import * as Status from "../../status";
import { noValueSign, separator } from '../../types';
import Locale from "../../utils/Locale";
import logger from "../../utils/Logger";
import * as Types from "../Types";
import { ValueAndUnit } from "../../types";
import { AlertRecord, AlertInfo }  from "../../alert/"
import * as Alert from "../../alert";

/** Base class */
export default class PoolDevice implements PoolDeviceInterface {    
    /** Minimum AutoFilterVersion where the device exists */
    static minAutoFilterVersion = 0;
    static CanBeAddedToflow = false;
    /** Hide from the picker. E.g. door sensor */
    static HideFromPicker = false;
    /** Icon to show in the picker and config screns */
    static Icon: string;
    /** Title to show when user picks it when ading a device e.g:  Locale.gettext('Actuator 2 Way'),*/
    static PickerTitle = Locale.gettext("Pool device");

    // Members holding data of all the devices


    allConfigs: AllConfig;
    allStates: Status.State[];
    siteStates: { [ key: string ]: any };

    allAlerts: AlertRecord[];

    deviceConfig: DeviceConfig;
    /** Device States e.g: { ActualPowerState: { name: "ActualPowerState", state: "ON", updated: "2024..." } } */
    deviceStates: StateMap;
    /** List of user initiated state changes. 
     * E.g: { PowerState: "ON" } */
    localStates = {};
    DeviceType: DeviceTypes;
    Name: string;
    UUID: string;
    
    /**
     * @param deviceConfig Section of the HardwareConfig related to the current device.
     * @param deviceStates States of the current device.
     * @param AutoFilterVersion Current AutoFilterVersion of the Controller
     * @param hardwareConfig Full HardwareConfig
     */
    constructor(deviceConfig:DeviceConfig, deviceStates: StateMap, siteStates: {}, allConfigs: AllConfig, allAlerts: AlertRecord[], allStates: Status.State[]) {
        this.init(deviceConfig, deviceStates, siteStates, allConfigs, allAlerts, allStates);
    }
    
    /** In case config or state changes in this function can update neccesarry member vars.
     * E.g: if SaltCall changes from serial to relay, we need different InformationFields
     */
    init(deviceConfig: DeviceConfig, deviceStates: StateMap, siteStates: {}, allConfigs: AllConfig, allAlerts: AlertRecord[], allStates: Status.State[]) {
        this.allConfigs = allConfigs;
        this.siteStates = siteStates;
        this.allAlerts = allAlerts;
        this.allStates = allStates;

        /* The app already have a state map for each device
        this.deviceStates.clear();
        for (let state of deviceStates) {
            this.deviceStates.set(state.name, state);
        }
        */
        this.deviceStates = deviceStates || {};

        this.deviceConfig = deviceConfig;
        this.Name = deviceConfig.Name ?? PoolDevice.PickerTitle;
        this.UUID = deviceConfig.UUID ?? uuid.v4();

        Object.keys(this.localStates).forEach(item => {
            const state = this.getDeviceState(item);
            /** @todo Bob SpeedIncrements are int so I send int - moreover my plus-minus control returns integer anyway but I get back string as DesiredSpeedPercent.... */
            // Not using strict comparison becaus Controller is not consistent with type
            if (this.localStates[ item ] == state?.value) {
                delete this.localStates[ item ];
            }
        });
    }

    get icon(): string {
        return (this.constructor as typeof PoolDevice).Icon;
    }

    canOverrideFromController() {
        // Temporarily we keep using internal information fields for PHProbe to workaround a Controller bug
        return true;
    }

    isBusy() {
        return false;
    }

    /**
     * @param Name PoolsideDaemonStateName E.g: DesiredSpeedPercent
     * @returns 
     */
    getDesiredValueOfCriticalField(Name: string) {
        const desiredStates = this.getDesiredValueOfCriticalFields();
        return desiredStates.find(item =>
            item.Name === Name
        )?.Value;
    }
    
    getDesiredValueOfCriticalFields(): { Name: string, Value: any }[] {
        const ret = [];
        this.getCriticalFields()?.forEach(itemCriticalField => {
            const state = this.getDeviceState(itemCriticalField.DesiredProperty.Name);
            ret.push({
                Name: itemCriticalField.DesiredProperty.Name,
                Value: state?.value
            });
        });
        return ret;
    }

    getCriticalFieldPropertyAndValue(Identifier: string): PoolDeviceCriticalFieldPropertyAndValue {
        const criticalField = this.getCriticalFields()?.find(item => item.Identifier === Identifier);
        if (!criticalField) {
            logger.warn(
                new Error("Could not find critical field by identifier: " + Identifier),
                "deviceConfig:", this.deviceConfig
            );
        } else {
            const controllerActual = this.getDeviceState(criticalField.ActualProperty.Name);
            const controllerDesried = this.getDeviceState(criticalField.DesiredProperty.Name);
            const localDesired = this.localStates[ criticalField.DesiredProperty.Name ] ?? controllerDesried?.value;

            const valueAndUnitActual = this.formatDeviceStatus(
                criticalField.ActualProperty.DisplayProcessingLogic,
                controllerActual?.value
            );
            const valueAndUnitDesired = this.formatDeviceStatus(
                criticalField.DesiredProperty.DisplayProcessingLogic,
                controllerDesried?.value
            );
            const valueAndUnitLocal =this.formatDeviceStatus(
                criticalField.DesiredProperty.DisplayProcessingLogic,
                localDesired
            )
            return {
                Identifier: criticalField.Identifier,
                ActualPropertyAndValue: {
                    Property: criticalField.ActualProperty,
                    Value: controllerActual?.value,
                    ValueAndUnit: valueAndUnitActual,
                    ValueFormatted: (valueAndUnitActual.value ?? "") + (valueAndUnitActual.separator ?? "") + (valueAndUnitActual.unit ?? ""),
                    Updated: controllerActual?.updated,
                },
                DesiredPropertyAndValue: {
                    Property: criticalField.DesiredProperty,
                    Value: controllerDesried?.value,
                    ValueAndUnit: valueAndUnitDesired,
                    ValueFormatted: (valueAndUnitDesired.value ?? "") + (valueAndUnitDesired.separator ?? "") + (valueAndUnitDesired.unit ?? ""),
                    Updated: controllerDesried?.updated,
                },
                LocalDesiredPropertyAndValue: {
                    Property: criticalField.DesiredProperty,
                    Value: localDesired,
                    ValueAndUnit: valueAndUnitLocal,
                    ValueFormatted: (valueAndUnitLocal.value ?? "") + (valueAndUnitLocal.separator ?? "") + (valueAndUnitLocal.unit ?? ""),
                    Updated: undefined
                }
            }
        }
    }

    getCriticalFieldsPropertyAndValue(): PoolDeviceCriticalFieldPropertyAndValue[] {
        return this.getCriticalFields()?.map(item => this.getCriticalFieldPropertyAndValue(item.Identifier));
    }

    getCriticalFieldDiff(CriticalFieldsPropertyAndValue: PoolDeviceCriticalFieldPropertyAndValue[]) {
        const identifiers = [];
        CriticalFieldsPropertyAndValue.forEach(item => {
            if (typeof item.DesiredPropertyAndValue.Value !== "undefined" && item.DesiredPropertyAndValue.Value !== item.ActualPropertyAndValue.Value) {
                identifiers.push(item.Identifier);
            }
        });
        return identifiers;
    }

    /** Filter out various disagreed Critical Fields that would confuse the users.
     * Override in child classes.
     * E.g. if it is an onoffable device e.g. pump, then filter out disagreed Pump Speed in case the pump is turned off */
    getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue: PoolDeviceCriticalFieldPropertyAndValue[]): string[] {
        const identifiers = this.getCriticalFieldDiff(CriticalFieldsPropertyAndValue);
        return identifiers.filter(item => {
            const field = this.getCriticalFieldPropertyAndValue(item);
            const now = new Date();
            // Remove field from the diff in case it is within the threshold
            if (
                (field.ActualPropertyAndValue.Property.DisplayDiffThreshold && (
                    field.ActualPropertyAndValue.Property.DisplayDiffThreshold >=
                    Math.abs(field.ActualPropertyAndValue.Value - field.DesiredPropertyAndValue.Value)
                )) ||
                (field.ActualPropertyAndValue.Property.DisplayDiffTimeout &&
                    (field.ActualPropertyAndValue.Property.DisplayDiffTimeout * 1000) >=
                    (now.getTime() - field.DesiredPropertyAndValue.Updated.getTime())
                )
            ) {
                return false;
            }
            return true;
        });
    }

    /**
     * Returns value of the given device state.
     * Also fixes types.
     * @param name State name as it called by the Poolside Controller E.g: ActualPowerState
     * @returns
     */
    getDeviceState(name: string): Types.DeviceState {
        const state = this.deviceStates[ name ];

        if (!state) return;
        const ret = {
            value: state.state as any,
            updated: new Date(state.updated)
        };
        if (ret.value === "null") {
            ret.value = null;
        } else if (ret.value === "false") {
            ret.value = false;
        } else if (ret.value === "true") {
            ret.value = true;
        } else if (!isNaN(ret.value)) {
            // Don't autoconvert number
            // ret.value = Number(ret.value);
        }
    
        return ret;
    }

    /**
     * @param displayName DisplayName or DispalyNameShort
     * @returns Localised version 
     */
    getDisplayNameTranslated(displayName:string): string {
        return displayName;
    }

    /**
     * Common formatting logic. Don't override in child classes!
     * @param DisplayProcessingLogic 
     * @param value 
     * @returns 
     */
    formatDeviceStatus(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        if (typeof value === "undefined" || value === null || value === "") {
            return { value: noValueSign };
        }
        return this.formatDeviceStatusCustom(DisplayProcessingLogic, value); 
    }

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {

        switch (DisplayProcessingLogic) {
        case DisplayProcessingLogics.AMP:
            return { value: Locale.formatNumber(value), unit: "A" };
        case DisplayProcessingLogics.UA:
            return { value: Locale.formatNumber(value), unit: "μA" };
        case DisplayProcessingLogics.DATETIME:
            return { value: Locale.formatDateTime(new Date(value)) };
        case DisplayProcessingLogics.ONOFF:
            if (value === true || value === "true" || value === PoolsideDaemonDeviceStateValues.ON) {
                return { value: Locale.gettext("On") };
            } else if (value === false || value === "false" || value === PoolsideDaemonDeviceStateValues.OFF) {
                return { value: Locale.gettext("Off") };
            } else if (value === PoolsideDaemonDeviceStateValues.UNKNOWN) {
                return { value: Locale.gettext("Unknown") };
            } else {
                return { value };
            }
        case DisplayProcessingLogics.TEMP_F:
            return Locale.formatTemperatureFromFahrenheit(value, undefined, undefined, undefined, undefined, true) as ValueAndUnit;
        case DisplayProcessingLogics.GPM:
            return Locale.formatVolumeCapacityFromGPM(value, undefined, undefined, true) as ValueAndUnit;
        case DisplayProcessingLogics.PERCENT:
            return { value: Locale.formatNumber(value, 2), separator: "", unit: "%" };
        case DisplayProcessingLogics.MG_L:
            return { value: Locale.formatNumber(value, 2), separator: " ", unit: "mg/L" };
        case DisplayProcessingLogics.X:
            // 1x, 2x, 3x
            return { value, separator: "", unit: "x" };
        case DisplayProcessingLogics.PPM:
            return { value: Locale.formatNumber(value, 3), separator: "", unit: "PPM" };
        case DisplayProcessingLogics.PSI:
            return Locale.formatPressureFromPsi(value, undefined, undefined, true, true) as ValueAndUnit;
        case DisplayProcessingLogics.MS_TO_S:
            return { value: Math.round(value / 1000), separator: " ", unit: "s" };
        case DisplayProcessingLogics.BOOLEAN:
            return { value: (value === true || value === "true") ? Locale.gettext("Yes") : Locale.gettext("No") };
        case DisplayProcessingLogics.FLOAT:
            return { value: Locale.formatNumber(value, 3) };
        case DisplayProcessingLogics.INTEGER:
            return { value: Locale.formatNumber(value, 0) };
        case DisplayProcessingLogics.MV:
            return { value: Locale.formatNumber(value, 3), separator: " ", unit: "mV" };
        case DisplayProcessingLogics.VOLT:
            return { value: Locale.formatNumber(value, 2), unit: "V" };
        case DisplayProcessingLogics.STRING:
        case DisplayProcessingLogics.LONG_STRING:
        default:
            return { value };
        }
    }

    getInformationFieldsFromStateOrInternal(): PoolDevicePropertyToDisplay[] {
        // Fix for the fact Stan reanemd the state name
        const deviceStatesToDisplayStateItem = this.getDeviceState(PoolsideDaemonDeviceStateNamesCommon.InformationFields)?.value;
        let deviceStatesToDisplay = (JSON.parse(deviceStatesToDisplayStateItem || "[]") || []) as StatesToDisplayItem[];
        
        if (
            deviceStatesToDisplay.length
            && this.canOverrideFromController()
        ) {
            // Controller provided list of states to display via the StatesToDisplay state item.
            deviceStatesToDisplay = deviceStatesToDisplay.filter(item => !item.FieldTypes || item.FieldTypes.includes(Types.StatesToDisplayItemFieldTypes.INFORMATION));
        } else {
            // Fallback to hard coded list of states to display
            deviceStatesToDisplay = this.getInformationFields() || [];
        }
        deviceStatesToDisplay.sort((a: PoolDevicePropertyToDisplay, b: PoolDevicePropertyToDisplay) => a.DisplayOrder > b.DisplayOrder ? 1 : -1);
        deviceStatesToDisplay.forEach(item => {
            item.DisplayName = this.getDisplayNameTranslated(item.DisplayName);
            item.DisplayNameShort = this.getDisplayNameTranslated(item.DisplayNameShort);
        });
        
        return deviceStatesToDisplay;
    }

    getMainMetricFieldsFromStateOrInternal(): StatesToDisplayItem {
        const deviceStatesToDisplayStateItem = this.getDeviceState(PoolsideDaemonDeviceStateNamesCommon.InformationFields)?.value;
        let deviceStatesToDisplay = (JSON.parse(deviceStatesToDisplayStateItem || "[]") || []) as StatesToDisplayItem[];

        if (
            deviceStatesToDisplay.length
            && this.canOverrideFromController()
        ) {
            // Controller provided list of states to display via the StatesToDisplay state item.

            const mainMetric = deviceStatesToDisplay.find(item => item.FieldTypes?.includes(Types.StatesToDisplayItemFieldTypes.MAIN_METRIC));
            if (mainMetric) {
                // Controller marked the main metric
                return mainMetric;
            }
        } else {
            // Fallback to hard coded list of states to display
            return this.getMainMetricField();
        }
    }

    getInformationFieldPropertyAndValue(poolsideDaemonDeviceStateName: string): PoolDevicePropertyAndValue {
        const fields = this.getInformationFieldsFromStateOrInternal()
        const field = fields?.find(item => item.Name === poolsideDaemonDeviceStateName);
        if (field) {
            return this.informationFieldToPoolDevicePropertyAndValue(field);
        }        
    } 

    /** Enrich field props with current value from states  */
    informationFieldToPoolDevicePropertyAndValue(field: PoolDevicePropertyToDisplay) {
        const state = this.getDeviceState(field.Name);
        const ValueAndUnit = this.formatDeviceStatus(
            field.DisplayProcessingLogic,
            state?.value
        );
        return {
            Property: field,
            Value: state?.value,
            ValueAndUnit,
            ValueFormatted: (ValueAndUnit.value ?? "") + (ValueAndUnit.separator ?? "") + (ValueAndUnit.unit ?? ""),
            Updated: state?.updated
        };
    }

    getInformationFieldsForDisplay(informationFields: PoolDevicePropertyToDisplay[]): PoolDevicePropertyAndValue[] {
        return informationFields?.map(itemInformationFields => this.informationFieldToPoolDevicePropertyAndValue(itemInformationFields)) || []
    }

    /** Filter out various Information Fields that would confuse the users.
     * Override in child classes.
     * E.g. if it is an onoffable device e.g. pump, then it does not make sense to show flow rate if the device is off */
    getInformationFieldsFromStateOrInternalForDisplay(): PoolDevicePropertyToDisplay[] {
        return this.getInformationFieldsFromStateOrInternal();
    }

    getDisplayInformation(): PoolDeviceDataToDisplay {
        const CriticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue();
        const CriticalFieldsDiff = this.getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue);

        const informationFields = this.getInformationFieldsFromStateOrInternalForDisplay();
        const informatinFieldsForDisplay = this.getInformationFieldsForDisplay(informationFields);
        const mainMetricField = this.getMainMetricFieldsFromStateOrInternal();

        let MainMetricField: Types.PoolDevicePropertyAndValue;
        if (mainMetricField) {
            const state = this.getDeviceState(mainMetricField.Name);
            const valueAndUnitActual = this.formatDeviceStatus(
                mainMetricField.DisplayProcessingLogic,
                state?.value
            );
            MainMetricField = {
                Property: mainMetricField,
                Value: state?.value,
                ValueAndUnit: valueAndUnitActual,
                ValueFormatted: (valueAndUnitActual.value ?? "") + (valueAndUnitActual.separator ?? "") + (valueAndUnitActual.unit ?? ""),
                Updated: state?.updated
            }
        }

        return {
            CriticalFields: CriticalFieldsPropertyAndValue,
            CriticalFieldsDiff,
            MainMetricField,
            InformationFields: informatinFieldsForDisplay,
        };
    }

    getCriticalFieldSummary(mode: criticalFieldSummaryModes): string {
        if (this.isWinterized()) {
            return Locale.gettext("Winterized");
        }
        return this.getCriticalFieldSummaryCustom();
    }

    getCriticalFieldSummaryCustom(): string {
        const criticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue() || [];
        return criticalFieldsPropertyAndValue.reduce((acc, curr) => (
            (acc ? (acc + " " + separator + " ") : "") +
            // curr.ActualPropertyAndValue.Property.DisplayNameShort + ': ' +
            curr.ActualPropertyAndValue.ValueFormatted
        ), "");
    }

    getErrorState() {
        return this.getDeviceState(Types.PoolsideDaemonDeviceStateNamesCommon.ErrorState)?.value
    }

    setCriticalField(Identifier: string, value: any) {
        const criticalField = this.getCriticalFieldPropertyAndValue(Identifier);
        this.localStates[ criticalField.DesiredPropertyAndValue.Property.Name ] = value;
    }

    /**
     * Returns list fo states that needs to be sent to the controller in order to set the user desired states
     */
    getStatesToSend() {
        const criticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue();
        return criticalFieldsPropertyAndValue.map(item => ({
            name: item.LocalDesiredPropertyAndValue.Property.Name,
            value: item.LocalDesiredPropertyAndValue.Value
        }));
    }

    getLocalStates() {
        return this.localStates;
    }

    isLocalStateMatchesDesired() {
        return Object.keys(this.localStates).length === 0;
    }

    clearLocalState() {
        this.localStates = {};
    }

    isWinterized(): boolean {
        return this.getDeviceState(PoolsideDaemonDeviceStateNamesCommon.Winterized)?.value;
    }

    /**
     * Calculates time when the internal logic of the component would return different data for the same input parameters.
     * This could be used in external components to tell when it would be useful to re-render.
     * E.g: getCriticalFieldDiffForDisplay() might return less items now than it would do 20sec later.
     * @returns Remaining time in milliseconds. Undefined otherwise.
     */
    getNextUpdate(): undefined | number {
        /** ms */
        let nextRenderIn: number;
        const CriticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue();
        const identifiers = this.getCriticalFieldDiff(CriticalFieldsPropertyAndValue);
        identifiers?.forEach(itemIdentifier => {
            if (!CriticalFieldsPropertyAndValue) return;

            const criticalField = CriticalFieldsPropertyAndValue.find(item => item.Identifier === itemIdentifier);
            if (criticalField.ActualPropertyAndValue.Property.DisplayDiffTimeout) {
                // So this is a field that currently differs and have timeout
                // that means once the timeout expires, the component needs a re-render
                // in order to show the disagreee box that was hidden so far
                const now = new Date();

                const remainingTime = criticalField.DesiredPropertyAndValue.Updated.getTime() + 
                    criticalField.ActualPropertyAndValue.Property.DisplayDiffTimeout * 1000 -
                    now.getTime()
                ;

                if (!remainingTime ||
                    (remainingTime > 0 && (
                        !nextRenderIn || remainingTime < nextRenderIn
                    ))
                ) {
                    nextRenderIn = remainingTime;
                }
                    
            }
        });
        return nextRenderIn;
    }

    /**
     * Returns Device Configuration converted into to the version specified by the passed in AutoFilterVersion
     * @param AutoFilterVersion Target version of the Device Configuration
     * @returns 
     */
    getConfig(AutoFilterVersion?: number): DeviceConfig {
        return this.deviceConfig;
    }

    /**
     * Wether to show the device under the Active tab of the DeviceStatus screen
     * @returns
     */
    isActive(): boolean {
        return false;
    }

    getAlerts(): AlertInfo[] {
        const allConfig = this.allConfigs;
        const alertInfo = [];
        const alertsSource = this.allAlerts?.filter(item => item.entityUuid === this.UUID) || [];
        const alerts = JSON.parse(JSON.stringify(alertsSource)) as AlertRecord[];
        alerts.forEach(itemAlerts => {
            alertInfo.push(Alert.getInfo(itemAlerts, allConfig, this.allStates));
        });

        // Alerts are also set as States
        const errorState = this.getErrorState();
        if (errorState &&
            ![
                Alert.HeaterErrorStates.None,
                Alert.PumpDriveErrorStates.None,
                Alert.HeatPumpErrorStates.NONE,
            ].includes(errorState) &&
            !alerts.some(item => item.type === errorState)
        ) {
            const alertMock = {
                type: errorState,
                entityUuid: this.UUID
            } as AlertRecord;
            alertInfo.push(Alert.getInfo(alertMock, allConfig, this.allStates));
        }

        return alertInfo;
    } 
        
    /** Returns fields holding auxulary data e.g: PSI, RPM of a pump device. */
    getInformationFields(): StatesToDisplayItem[] {
        return [];
    }

    /** Returns fields holding the main metric to display on the Device Status screen at the top right corner.
     * Only devices that does not have power on/off control has this.
     * E.g: temperature of a thermistor device. */
    getMainMetricField(): StatesToDisplayItem {
        return;
    }

    /** Returns list of changeable states and states to display on the Device Status screen, below the device name. */
    getCriticalFields(): PoolDeviceCriticalDesiredActual[] {
        return [];
    }
}

